import { alert } from "../../../utils/APICalls/APICalls";
import { AlertErrorType } from "../../../utils/ErrorTypes";
import Offer from "../../../utils/OfferClass/Offer/Offer";
import { createOffer } from "../../../utils/OfferClass/OfferClassUtils/OfferCreator/offerCreator";
import { OrderTypesEnum } from "../../../utils/enums";
import { OfferType, PAGER_DUTY_ERROR_URGENCY } from "../../../utils/types";

export type TransformOfferType<T> = T extends OfferType[]
  ? Offer[]
  : T extends OfferType
  ? Offer
  : T extends string
  ? T extends keyof typeof OrderTypesEnum
    ? T
    : T
  : T extends number
  ? number // Return number type directly
  : T extends object
  ? TransformOfferTypeObject<T>
  : T;

export type TransformOfferTypeObject<T> = {
  [K in keyof T]: TransformOfferType<T[K]>;
};

export function transformObject<T>(obj: T): T {
  try {
    if (Array.isArray(obj)) {
      return obj.map((item) => transformObject(item)) as unknown as T;
    } else if (typeof obj === "object" && obj !== null) {
      const newObj: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          newObj[key] = transformObject(obj[key]);
        }
      }

      if (newObj.offerType && !isOfferType(newObj)) {
        alert(
          `${AlertErrorType.Error_Identifying_Offer}`,
          { newObj },
          PAGER_DUTY_ERROR_URGENCY.HIGH
        );
      }
      if (isOfferType(newObj)) {
        return createOffer(newObj) as unknown as T;
      }

      return newObj as T;
    } else if (typeof obj === "string" && isEnumValue(obj)) {
      return obj;
    } else if (typeof obj === "number") {
      return obj;
    } else {
      return obj;
    }
  } catch (error) {
    alert(
      `${AlertErrorType.Error_In_TransformObject}`,
      { obj, error },
      PAGER_DUTY_ERROR_URGENCY.HIGH
    );

    return obj;
  }
}

function isEnumValue(value: any): boolean {
  return typeof value === "string" && value in OrderTypesEnum;
}

function isOfferType(obj: any): obj is OfferType {
  return (
    Object.prototype.hasOwnProperty.call(obj, "id") &&
    Object.prototype.hasOwnProperty.call(obj, "offerType")
  );
}

type ReplaceNullWithUndefined<T> = T extends Array<infer U>
  ? ReplaceNullWithUndefined<U>[]
  : T extends object
  ? { [K in keyof T]: ReplaceNullWithUndefined<T[K]> }
  : T extends null
  ? undefined
  : T;

export function replaceNullWithUndefined<T>(
  obj: T
): ReplaceNullWithUndefined<T> {
  if (Array.isArray(obj)) {
    // If it's an array, recursively process each element
    return obj.map((item) =>
      replaceNullWithUndefined(item)
    ) as ReplaceNullWithUndefined<T>;
  } else if (obj !== null && typeof obj === "object") {
    // If it's an object, process its properties
    return Object.entries(obj).reduce((acc, [key, value]) => {
      acc[key] = value === null ? undefined : replaceNullWithUndefined(value);

      return acc;
    }, {} as any) as ReplaceNullWithUndefined<T>;
  }

  // Return null as undefined, or non-object values as is
  return (obj === null ? undefined : obj) as ReplaceNullWithUndefined<T>;
}
